// The TEAM (Signal) view vocabulary — pure display projections over the closed directory vocabularies.
// Access labels restate the three permission levels while the authority metadata gives each CLOSED key
// its plain-English line. An
// authority key the meta does not know passes through with a mechanical label (G-197: the client widens
// nothing and hides nothing the projection carries). Pure — no I/O, no state.

import type { DirectoryRole, UserRow } from '../know-projections.ts';

/** Access labels — deliberately never called company roles; those live in `responsibilities`. */
export const ACCESS_LEVEL_LABEL: Readonly<Record<DirectoryRole, string>> = {
  admin_md: 'Company admin',
  operator: 'Ritona operator',
  member: 'Team member',
};

/** The three access levels in the order the selector offers them (most authority first). */
export const ACCESS_LEVEL_ORDER: readonly DirectoryRole[] = ['admin_md', 'operator', 'member'];

/** Human-editable person fields. Access role stays separately named at each rendered control. */
export interface EditablePersonPatch {
  readonly displayName?: string;
  readonly responsibilities?: string;
  readonly role?: DirectoryRole;
}

/** One rendered sign-off row — the capability key restated in plain English + the recorded grant. */
export interface AuthorityRowView {
  readonly key: string;
  readonly label: string;
  /** The plain-English consequence line, or null for a key outside the known meta (mechanical label). */
  readonly sub: string | null;
  readonly granted: boolean;
}

/** The known capability keys' display copy, in the screen's presentation order (identity's closed
 *  vocabulary, `authority-profile.ts`) — what a grant MEANS, not how it is enforced. */
const AUTHORITY_META: ReadonlyArray<{
  readonly key: string;
  readonly label: string;
  readonly sub: string;
}> = [
  {
    key: 'may_approve_metric_value',
    label: 'Sign off KPI figures',
    sub: 'Their yes makes a number the official one.',
  },
  {
    key: 'may_activate_metric',
    label: 'Switch KPIs on',
    sub: 'Turn a drafted KPI into a live one.',
  },
  {
    key: 'may_activate_schedule',
    label: 'Switch routines on',
    sub: 'Put a drafted routine into the calendar.',
  },
  {
    key: 'may_approve_memory_promotion',
    label: 'Approve Ritona’s memory',
    sub: 'Decide what it keeps permanently in mind.',
  },
  {
    key: 'may_administer_directory',
    label: 'Save team changes',
    sub: 'Commit changes to people and access — this page.',
  },
  {
    key: 'may_grant_write_scope',
    label: 'Grant write access',
    sub: 'Let Ritona change records in the connected systems.',
  },
  {
    key: 'may_set_business_definitions',
    label: 'Own business definitions',
    sub: 'Decide what terms like “active customer” mean.',
  },
  {
    key: 'may_perform_destructive_operation',
    label: 'Destructive operations',
    sub: 'Deletions and other acts that can’t be undone.',
  },
];

/** A mechanical fallback label for a capability key outside the known meta (display only). */
function mechanicalLabel(key: string): string {
  return key.replace(/^may_/, '').replace(/_/g, ' ');
}

/**
 * The sign-off rows for one profile: the KNOWN keys first, in the meta's presentation order (only those
 * the profile actually carries), then any key the meta does not know, in the profile's own order — the
 * rendered list always covers EXACTLY the projection's keys.
 */
export function authorityRows(
  profile: Readonly<Record<string, boolean>>,
): readonly AuthorityRowView[] {
  const known = AUTHORITY_META.filter((meta) => meta.key in profile).map((meta) => ({
    key: meta.key,
    label: meta.label,
    sub: meta.sub,
    granted: profile[meta.key] === true,
  }));
  const unknown = Object.entries(profile)
    .filter(([key]) => !AUTHORITY_META.some((meta) => meta.key === key))
    .map(([key, granted]) => ({ key, label: mechanicalLabel(key), sub: null, granted }));
  return [...known, ...unknown];
}

/** The person's first name for the section headings (falls back to the full display name). */
export function firstNameOf(user: UserRow): string {
  const first = user.displayName.trim().split(/\s+/)[0];
  return first !== undefined && first.length > 0 ? first : user.displayName;
}
